import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Strip emoji characters (not supported by built-in PDF fonts)
const stripEmoji = (str) =>
  str.replace(/[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\uFE0F\u20E3]/gu, '').trim();

// Parse inline **bold** tokens → array of <Text> nodes for react-pdf
const parseInline = (rawText) => {
  const text = stripEmoji(rawText);
  const segments = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;
  let keyCounter = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        <Text key={keyCounter++}>{text.slice(lastIndex, match.index)}</Text>
      );
    }
    segments.push(
      <Text key={keyCounter++} style={{ fontFamily: 'Helvetica-Bold' }}>
        {match[1]}
      </Text>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push(<Text key={keyCounter++}>{text.slice(lastIndex)}</Text>);
  }

  return segments.length > 0 ? segments : text;
};

const TEAL = '#0d9488';
const TEAL_DARK = '#134e4a';
const GRAY = '#374151';
const LIGHT_GRAY = '#6b7280';
const BORDER = '#e5e7eb';

const styles = StyleSheet.create({
  page: {
    paddingTop: 44,
    paddingBottom: 44,
    paddingHorizontal: 52,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: GRAY,
    lineHeight: 1.55,
  },
  h1: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a2e',
    marginBottom: 8,
    marginTop: 0,
    paddingBottom: 4,
    borderBottomWidth: 1.5,
    borderBottomColor: TEAL,
    borderBottomStyle: 'solid',
  },
  h2: {
    fontSize: 12.5,
    fontFamily: 'Helvetica-Bold',
    color: TEAL,
    marginBottom: 5,
    marginTop: 14,
  },
  h3: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: TEAL_DARK,
    marginBottom: 4,
    marginTop: 11,
  },
  h4: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: GRAY,
    marginBottom: 3,
    marginTop: 8,
  },
  paragraph: {
    marginBottom: 4,
  },
  bullet: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 8,
  },
  bulletDot: {
    width: 12,
    fontFamily: 'Helvetica-Bold',
    color: TEAL,
    lineHeight: 1.55,
  },
  bulletText: {
    flex: 1,
  },
  subBullet: {
    flexDirection: 'row',
    marginBottom: 2,
    paddingLeft: 22,
  },
  subBulletDot: {
    width: 12,
    color: LIGHT_GRAY,
    lineHeight: 1.55,
  },
  subBulletText: {
    flex: 1,
    fontSize: 9.5,
    color: LIGHT_GRAY,
  },
  blockquote: {
    borderLeftWidth: 2.5,
    borderLeftColor: TEAL,
    borderLeftStyle: 'solid',
    paddingLeft: 10,
    paddingVertical: 4,
    marginLeft: 4,
    marginBottom: 6,
    backgroundColor: '#f0fdf4',
  },
  blockquoteText: {
    fontSize: 9.5,
    color: TEAL_DARK,
    fontStyle: 'italic',
  },
  hr: {
    borderTopWidth: 0.5,
    borderTopColor: BORDER,
    borderTopStyle: 'solid',
    marginVertical: 8,
  },
});

const renderLine = (line, key) => {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Horizontal rule
  if (trimmed === '---') return <View key={key} style={styles.hr} />;

  // Headings
  if (line.startsWith('#### '))
    return <Text key={key} style={styles.h4}>{parseInline(line.slice(5))}</Text>;
  if (line.startsWith('### '))
    return <Text key={key} style={styles.h3}>{parseInline(line.slice(4))}</Text>;
  if (line.startsWith('## '))
    return <Text key={key} style={styles.h2}>{parseInline(line.slice(3))}</Text>;
  if (line.startsWith('# '))
    return <Text key={key} style={styles.h1}>{parseInline(line.slice(2))}</Text>;

  // Blockquote
  if (line.startsWith('> '))
    return (
      <View key={key} style={styles.blockquote}>
        <Text style={styles.blockquoteText}>{parseInline(line.slice(2))}</Text>
      </View>
    );

  // Sub-bullet (2+ spaces indent)
  if (/^  +[-*] /.test(line)) {
    const text = line.replace(/^  +[-*] /, '');
    return (
      <View key={key} style={styles.subBullet}>
        <Text style={styles.subBulletDot}>o</Text>
        <Text style={styles.subBulletText}>{parseInline(text)}</Text>
      </View>
    );
  }

  // Top-level bullet
  if (line.startsWith('- ') || line.startsWith('* '))
    return (
      <View key={key} style={styles.bullet}>
        <Text style={styles.bulletDot}>•</Text>
        <Text style={styles.bulletText}>{parseInline(line.slice(2))}</Text>
      </View>
    );

  // Paragraph
  return (
    <Text key={key} style={styles.paragraph}>
      {parseInline(line)}
    </Text>
  );
};

const InterviewScriptDocument = ({ content, title = "Script d'Entretien" }) => {
  const lines = content.split('\n');
  const elements = lines.map((line, i) => renderLine(line, i)).filter(Boolean);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {elements}
      </Page>
    </Document>
  );
};

export default InterviewScriptDocument;
